<template>
  <section class="section">
    <div class="container">
      <Title>API documentation</Title>

      <p>
        API base URL:
        <b class="has-text-primary">{{ getUrl() }}</b
        >.
      </p>

      <Article title="Start here">
        <p>Start with adding a key by clicking on "Add key".</p>

        <section class="box">
          <ApiKeys></ApiKeys>
        </section>

        <p>
          Use the key for authentication, in the request header. The header name
          is <b class="has-text-primary">x-api-key</b>.
        </p>

        <p>
          It's easier to use
          <a href="https://www.postman.com/downloads/" target="_blank"
            >Postman</a
          >
          or <a href="https://insomnia.rest/download">Insomnia</a> when trying
          out more advanced requests.
        </p>
      </Article>

      <Article title="GET: /sources">
        <p>Returning all sources/repos. Includes repos shared with you.</p>

        <p>Example response:</p>
        <pre class="notification is-info is-light">{{
          JSON.stringify(
            [
              {
                shared: null,
                uid: "000000-0000-4edb-00000-000000000",
                css: "black",
                external: false,
                name: "My repo",
                description: "Private feed",
                permission: 32,
                version: 2,
                url: "",
                key: "longkey",
              },
              {
                shared: null,
                uid: "2f0d2190-e5ee-431c-854a-f73caf4731dc",
                css: "blue darken-1",
                external: true,
                name: "Chocolatey",
                description: "Community feed",
                permission: 32,
                version: null,
                url: "https://chocolatey.org/api/v2/",
                key: "",
              },
            ],
            undefined,
            2
          )
        }}</pre>
      </Article>

      <Article title="GET: /domains">
        <p>Returning all domains. Includes domains shared with you.</p>

        <p>Example response:</p>
        <pre class="notification is-info is-light">{{
          JSON.stringify(
            [
              {
                shared: null,
                uid: "c34d45cc-1873-4793-9cdb-427bc0587a50",
                description: "",
                permission: null,
                friendlyName: "Company co.",
                key: "averylongkey",
                sharedWithOthers: false,
              },
            ],
            undefined,
            2
          )
        }}</pre>
      </Article>

      <Article title="GET: /domains/:domainUID/groups">
        <p>
          Returning all groups. Includes groups shared with you. "domainUID" is
          the uid of a domain.
        </p>

        <p>Full path example:</p>
        <pre>/domains/c34d45cc-1873-4793-9cdb-427bc0587a50/groups</pre>

        <p>Example response:</p>
        <pre class="notification is-info is-light">{{
          JSON.stringify(
            [
              {
                id: 183959,
                name: "Office",
                uid: "6cba6338-3250-4859-a1ae-7ac042f4e503",
                css: "cyan-text lighten-1",
              },
              {
                id: 184774,
                name: "General applications",
                uid: "45067282-423b-4d03-8ba3-0c5d6c68800e",
                css: "green-text lighten-1",
              },
            ],
            undefined,
            2
          )
        }}</pre>
      </Article>

      <Article title="GET: /domains/:domainUID/groups-computers">
        <p>
          Returning all groups and all computers per group. "domainUID" is the
          uid of a domain.
        </p>

        <p>Full path example:</p>
        <pre>
/domains/c34d45cc-1873-4793-9cdb-427bc0587a50/groups-computers</pre
        >

        <p>Example response:</p>
        <pre class="notification is-info is-light">{{
          JSON.stringify(
            [
              {
                group: {
                  id: 183959,
                  name: "Office",
                  uid: "6cba6338-3250-4859-a1ae-7ac042f4e523",
                  css: "cyan-text lighten-1",
                },
                computers: [
                  {
                    systemInfo: {
                      macAddresses: ["5CEB42076001"],
                    },
                    uid: "781a8fe6-d7fc-22f9-494e-e44b3e4ed001",
                    onlineChanged: "1596462979970",
                    name: "XPS-DATOR",
                    online: false,
                    clientVersion: "0.10.2.0",
                    friendlyName: "Jonas computer",
                    inventorying: false,
                  },
                ],
              },
            ],
            undefined,
            2
          )
        }}</pre>
      </Article>

      <Article title="GET: /domains/:domainUID/computers">
        <p>
          Returning all computers in a domain. "domainUID" is the uid of a
          domain.
        </p>

        <p>Full path example:</p>
        <pre>/domains/c34d45cc-1873-4793-9cdb-427bc0587a50/computers</pre>

        <p>Example response:</p>
        <pre class="notification is-info is-light">{{
          JSON.stringify(
            [
              {
                systemInfo: {
                  macAddresses: ["5CEB42076001"],
                },
                uid: "781a8fe6-d7fc-22f9-494e-e44b3e4ed001",
                onlineChanged: "1596462979970",
                name: "XPS-DATOR",
                online: false,
                clientVersion: "0.10.2.0",
                friendlyName: "Jonas computer",
                inventorying: false,
              },
            ],
            undefined,
            2
          )
        }}</pre>
      </Article>

      <Article title="POST: /domains/:domainUID/package">
        <p>
          Install/uninstall package. These request mirrors the behavior of the
          website.
        </p>

        <p>Full path example:</p>
        <pre>/domains/c34d45cc-1873-4793-9cdb-427bc0587a50/package</pre>

        <section class="content">
          <ul>
            <li>
              Success status code:
              <b class="has-text-success">202</b>
            </li>
            <li>
              Error status code:
              <b class="has-text-danger">400</b>
            </li>
          </ul>
        </section>

        <p>Parameters explained:</p>
        <section class="content">
          <ul>
            <li>
              <span class="tag is-info smaller">Targets computers</span>
              computerUIDs: String array with computer UIDs. UIDs can be found
              in <i>/domains/:domainUID/computers</i>
            </li>
            <li>
              <span class="tag is-info smaller">Targets a group</span>
              groupUID: String. UID of a group found in
              <i>/domains/:domainUID/groups</i>
            </li>
            <li>
              package: Object
              <ul>
                <li>
                  id: String. Package id, same as when using choco locally
                </li>
                <li>
                  version: String. Package version, same as when using choco
                  locally
                </li>
                <li>
                  command: String. Either install, uninstall, or upgrade
                </li>
                <li>
                  sourceUID: String. UID of a source found in
                  <i>/sources</i>
                </li>
              </ul>
            </li>
            <li>
              <span class="tag is-info">Optional</span>
              <span class="ml-2 tag is-info">Only for groups</span>
              schedule: Object
              <ul>
                <li>startDate: String. <code>{year} {month} {date}</code></li>
                <li>endDate: String. <code>{year} {month} {date}</code></li>
                <li>startTime: String. <code>{hour}:{minute}</code></li>
                <li>endTime: String. <code>{hour}:{minute}</code></li>
                <li>
                  repeatWeekDays: String.
                  <code>{dayofweek}|{dayofweek}|{dayofweek}</code>. 1 = monday,
                  7 = sunday.
                </li>
                <li>
                  autoUpgrade: Boolean. Auto upgrade packages inside the scope
                  of the schedule.
                </li>
              </ul>
            </li>
          </ul>
        </section>

        <p>Request body example (targeting computers):</p>
        <pre class="notification is-info is-light">{{
          JSON.stringify(
            {
              computerUIDs: ["781a8fe6-d7fc-22f9-494e-e44b3e4ed001"],
              package: {
                id: "notepad3.install",
                version: "6.23.203.2",
                command: "install",
                sourceUID: "000000-0000-4edb-00000-000000000",
              },
            },
            undefined,
            2
          )
        }}</pre>

        <p>
          Request body example (targeting a group). Schedule is optional.
        </p>
        <pre class="notification is-info is-light">{{
          JSON.stringify(
            {
              groupUID: ["6cba6338-3250-4859-a1ae-7ac042f4e503"],
              package: {
                id: "notepad3.install",
                version: "6.23.203.2",
                command: "install",
                sourceUID: "000000-0000-4edb-00000-000000000",
              },
              schedule: {
                startDate: "2023 07 08",
                endDate: "2030 07 08",
                startTime: "14:00",
                endTime: "12:00",
                repeatWeekDays: "1|2|7",
                autoUpgrade: false,
              },
            },
            undefined,
            2
          )
        }}</pre>

        <p>Error response example:</p>
        <pre>{{
          JSON.stringify(
            {
              message:
                "The package was not found in repo with param sourceUID: 2f0d2190-e5ee-431c-854a-f73caf4731dc",
            },
            undefined,
            2
          )
        }}</pre>
      </Article>
    </div>
  </section>
</template>

<script>
export default {
  props: {
    value: Boolean,
  },
  components: {
    Title: () => import("@/components/singles/Title.vue"),
    ApiKeys: () => import("@/components/main/user/ApiKeys.vue"),
    Article: () => import("@/components/singles/Article.vue"),
  },
  data() {
    return {};
  },

  methods: {
    getUrl() {
      return `${location.protocol}//${this.$store.state.settings.server.baseApi}/secure`;
    },
  },
};
</script>

<style scoped>
p:not(:first-child) {
  margin-top: 20px;
}

p {
  margin-bottom: 20px;
}

::v-deep pre {
  background-color: hsl(0deg, 0%, 96%);
}

pre {
  background-color: hsl(0deg, 0%, 90%) !important;
}
</style>
